import { UserProfile, IdealCustomerProfile, LeadSignal, UserActionRequest, UserActionResponse, LeadTable, NotificationPreferencesResponse, NotificationPreferenceUpdate } from '../types';
import { ChatResponse } from '../types/chat';
import { CreateProjectRequest, GTMProject, GTMProjectTransition, PipelineBoard, CampaignBrief, PipelineStage, ProjectRunsResponse, StageSkillsResponse, ProjectLeadList, ProjectCopyVariant, LeadListMember, TakeoverFrame, TakeoverInput } from '../types/pipeline';
import {
  AssignableCampaign,
  MailboxAssignResult,
  MailboxAssignUpdates,
  MailboxRow,
  MailboxSyncResult,
} from '../types/mailbox';
import { InvitePreview, WorkspaceMember, WorkspaceInvite } from '../types/workspace';
import { AccountListRow, ClientEntityRow, AccountDetail, ReplyItem, CampaignsOverview, WorkspaceMember as AdminWorkspaceMember, EntityBrief, TimelineResponse, CampaignHistoryItem } from '../types/accounts';
import {
  AdminOrganization,
  AdminSignupApproval,
  AdminSignupsPage,
  AdminUserWorkspaceMapping,
  SlackChannelMapping,
} from '../types/admin';
import { AgentUsageRollup, MarginQuery, MarginRollup } from '../types/agentUsage';
import { SlackChannelOption, SlackStatus } from '../types/slack';
import { StandupDigest } from '../types/standup';
import { supabase } from './supabase';
import { httpClient } from './httpClient';
import { NOT_ENOUGH_CREDITS_ERROR, OUT_OF_CREDITS_ERROR_MESSAGE } from '../constants/error-messages';
import { hashCriterionTextSync } from '../utils/tableUtils';

/**
 * Helper function to derive suggested action from error status code.
 * This maps status codes to user-friendly action guidance.
 * Note: suggested_action is NOT stored in the database - it's derived from status.
 * Exported for use in components that need to derive actions from status updates.
 */
export function getSuggestedActionFromStatus(status: string | undefined): string | undefined {
  if (!status) return undefined;

  // Keep messages aligned with backend ERROR_MESSAGES in constants/errors.py
  const actionMap: Record<string, string> = {
    'error_provider_api': 'Please try again in a few moments. If the issue persists, contact support.',
    'error_timeout': 'Try a more specific search query or reduce the number of rows requested.',
    'error_rate_limit': 'Wait a few minutes before trying again.',
    'error_invalid_query': 'Check your search terms and try rephrasing your query.',
    'error_server': 'Please try again. If the problem continues, contact support with the error details.',
    'error_config': 'Please contact support for assistance.',
    'error': 'Please try again or contact support if the issue persists.',
    'completed_no_results': 'Try broadening your search criteria or using different keywords.'
  };

  return actionMap[status];
}

// ---- Calendar types -------------------------------------------------------

export interface CalendarEventAttendee {
  email: string;
  display_name?: string | null;
  response_status?: string | null;
}

export interface CalendarEvent {
  id: string;
  google_event_id: string;
  credential_id: string;
  title: string | null;
  description: string | null;
  location: string | null;
  start_time: string;
  end_time: string;
  all_day: boolean;
  attendees: CalendarEventAttendee[];
  meet_link: string | null;
  status: string;
  organizer_email: string | null;
  response_status: string | null;
  account_email: string | null;
  synced_at: string | null;
}

export interface CalendarEventsResponse {
  events: CalendarEvent[];
  count: number;
  last_synced_at: string | null;
}

export interface SetPrimaryCalendarResponse {
  success: boolean;
  message: string;
}

export interface CreatedCalendarEvent {
  google_event_id: string;
  title: string | null;
  start_time: string;
  end_time: string;
  meet_link: string | null;
  html_link: string | null;
}

// ---------------------------------------------------------------------------

class ApiClient {
  async getTableLeads(tableId: string) {
    try {
      const { data: table } = await supabase
        .from('lead_tables')
        .select('*')
        .eq('id', tableId)
        .single();

      if (!table) {
        return { leads: [], columns: [], status: 'error' };
      }

      const [
        { data: columns },
        { data: rows }
      ] = await Promise.all([
        supabase
          .from('lead_columns')
          .select('*')
          .eq('lead_table_id', tableId)
          .order('display_order'),
        supabase
          .from('lead_rows')
          .select('*')
          .eq('lead_table_id', tableId)
          .order('created_at')
      ]);

      if (!rows || rows.length === 0) {
        return {
          leads: [],
          columns: columns?.map(col => col.name) || [],
          prospects_analyzed: table.prospects_analyzed || null,
          status: table.status || 'completed',
          name: table.name || 'Dataset',
          table_type: table.table_type || 'companies'
        };
      }

      const rowIds = rows.map(r => r.id);
      const [
        { data: cells },
        { data: reasoningData }
      ] = await Promise.all([
        supabase
          .from('lead_cells')
          .select(`
            *,
            lead_columns(name)
          `)
          .in('row_id', rowIds),
        supabase
          .from('lead_row_reasoning')
          .select(`
            lead_row_id,
            criterion_text,
            reasoning_text,
            satisfied,
            data_sources
          `)
          .in('lead_row_id', rowIds)
      ]);

      const leads = rows?.map(row => {
        const rowCells = cells?.filter(cell => cell.row_id === row.id) || [];

        const data: Record<string, any> = {};
        const cellStates: Record<string, any> = {};
        const rowMetadata = row.reasoning || {};
        const metadata = {
          displayUrl: rowMetadata.display_url || null,
          entityType: row.entity_type
        };

        rowCells.forEach(cell => {
          if (cell.lead_columns) {
            // Handle both object and array returns from Supabase join
            const columnName = Array.isArray(cell.lead_columns)
              ? cell.lead_columns[0]?.name
              : cell.lead_columns?.name;
            if (columnName) {
              data[columnName] = cell.value;
              cellStates[columnName] = {
                isLoading: cell.is_loading || false
              };
            }
          }
        });

        const reasoning: Record<string, any> = {};
        const rowReasoningItems = reasoningData?.filter(r => r.lead_row_id === row.id) || [];

        rowReasoningItems.forEach((reasoningItem: any, index) => {
          const criterionText = reasoningItem.criterion_text;
          const key = criterionText
            ? hashCriterionTextSync(criterionText)
            : (reasoningItem as any).segment_id || `criterion_${index}`;
          reasoning[key] = {
            reasoning_text: reasoningItem.reasoning_text,
            satisfied: reasoningItem.satisfied,
            source_urls: reasoningItem.data_sources || [],
            segment_text: reasoningItem.criterion_text,
            segment_order: null,
            criterion_text: reasoningItem.criterion_text
          };
        });

        return {
          id: row.id,
          type: row.entity_type,
          name: data.Name || data.name || 'Unknown',
          data,
          cellStates,
          metadata,
          reasoning,
          createdAt: row.created_at,
          sourceType: row.source_type,
          sourceId: row.source_id,
        };
      }) || [];

      return {
        leads,
        columns: columns?.map(col => col.name) || [],
        prospects_analyzed: table.prospects_analyzed || null,
        status: table.status || 'completed',
        name: table.name || 'Dataset', // Include table name for TopBar display
        table_type: table.table_type || 'companies', // Include table type for email variables
        error_message: table.error_message,
        suggested_action: getSuggestedActionFromStatus(table.status)
      };
    } catch (error) {
      console.error('Error getting table leads:', error);
      return { leads: [], columns: [], status: 'error' };
    }
  }

  /**
   * Fetch columns for a lead table with full metadata (key, name, column_type)
   * Note: key is set to column name for backend compatibility (backend matches by name)
   */
  async getTableColumns(tableId: string): Promise<{ columns: Array<{ key: string; name: string; column_type?: string }> }> {
    try {
      const { data: columns, error } = await supabase
        .from('lead_columns')
        .select('id, name, column_type, display_order')
        .eq('lead_table_id', tableId)
        .order('display_order');

      if (error) throw error;

      return {
        columns: (columns || []).map(col => ({
          key: col.name, // Use name as key for backend matching
          name: col.name,
          column_type: col.column_type,
        })),
      };
    } catch (error) {
      console.error('Error getting table columns:', error);
      return { columns: [] };
    }
  }

  /**
   * Fetch enrichment metadata on-demand (for CSV export, tooltips, etc.)
   * Not loaded by default to improve performance
   */
  async getEnrichmentMetadata(
    _tableId: string,
    rowIds: string[],
    columnIds?: string[]
  ): Promise<Record<string, Record<string, import('../types/table').EnrichmentMetadata>>> {
    try {
      let query = supabase
        .from('lead_cells')
        .select(`
          row_id,
          lead_columns(name),
          enrichment_summary,
          enrichment_source_url,
          enrichment_confidence_score,
          data_sources
        `)
        .in('row_id', rowIds);

      if (columnIds) {
        query = query.in('column_id', columnIds);
      }

      const { data: cells } = await query;

      const metadata: Record<string, Record<string, any>> = {};

      cells?.forEach((cell: any) => {
        if (!metadata[cell.row_id]) {
          metadata[cell.row_id] = {};
        }

        const columnName = Array.isArray(cell.lead_columns) ? cell.lead_columns[0]?.name : cell.lead_columns?.name;
        if (columnName) {
          metadata[cell.row_id][columnName] = {
            summary: cell.enrichment_summary,
            sourceUrl: cell.enrichment_source_url,
            confidenceScore: cell.enrichment_confidence_score,
            dataSources: cell.data_sources || []
          };
        }
      });

      return metadata;
    } catch (error) {
      console.error('Error fetching enrichment metadata:', error);
      return {};
    }
  }

  async getLeadTablesPaginated(limit: number = 20, offset: number = 0): Promise<{ tables: any[], hasMore: boolean }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { tables: [], hasMore: false };

      const { data: tables } = await supabase
        .from('lead_tables')
        .select(`
          *,
          lead_rows(count)
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .range(offset, offset + limit);

      const processedTables = tables?.slice(0, limit)?.map(table => {
        // Use new structure (total_found) first, fallback to old structure (progress.found)
        const metadataCount = table.provider_metadata?.total_found ?? table.provider_metadata?.progress?.found;

        let aggregatedCount: number | undefined;
        if (Array.isArray(table.lead_rows)) {
          if (table.lead_rows.length > 0 && typeof table.lead_rows[0]?.count === 'number') {
            aggregatedCount = table.lead_rows[0].count;
          } else if (table.lead_rows.length > 0) {
            aggregatedCount = table.lead_rows.length;
          }
        } else if (typeof table.lead_rows === 'object' && table.lead_rows !== null) {
          aggregatedCount = (table.lead_rows as any).count;
        }

        // Use provider metadata if available and positive, otherwise use database count
        const lead_count = (metadataCount !== undefined && metadataCount > 0)
          ? metadataCount
          : (aggregatedCount ?? 0);

        return {
          ...table,
          lead_count,
          prospects_analyzed: table.provider_metadata?.total_analyzed ?? table.provider_metadata?.progress?.analyzed ?? table.prospects_analyzed ?? null
        };
      }) || [];

      return {
        tables: processedTables,
        hasMore: (tables?.length ?? 0) > limit
      };
    } catch (error) {
      console.error('Error getting paginated lead tables:', error);
      return { tables: [], hasMore: false };
    }
  }

  async deleteLeadTable(id: string): Promise<void> {
    await httpClient.delete(`/lead-tables-v2/${id}`);
  }

  async renameDatasetColumn(tableId: string, name: string, newName: string): Promise<void> {
    await httpClient.patch(`/lead-tables-v2/${tableId}/columns`, { name, new_name: newName });
  }

  async deleteDatasetColumn(tableId: string, name: string): Promise<void> {
    await httpClient.delete(`/lead-tables-v2/${tableId}/columns`, { params: { name } });
  }

  async refreshProviderResults(tableId: string): Promise<{
    success: boolean;
    message: string;
    items_added: number;
    items_updated: number;
    total_columns?: number;
  }> {
    try {
      const response = await httpClient.post(`/lead-tables/refresh-provider-results/${tableId}`);
      return response.data;
    } catch (error) {
      console.error('Error refreshing provider results:', error);
      throw error;
    }
  }

  async updateUserProfile(profile: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const response = await httpClient.put('/users/profile', profile);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // ICP endpoints - Using backend API
  async getICPProfiles(): Promise<IdealCustomerProfile[]> {
    try {
      const response = await httpClient.get('/signals/icps');
      return response.data.profiles || [];
    } catch (error) {
      console.error('Error getting ICP profiles:', error);
      return [];
    }
  }

  async createICPProfile(profile: Partial<IdealCustomerProfile>): Promise<IdealCustomerProfile> {
    try {
      const response = await httpClient.post('/signals/icps', profile);
      return response.data;
    } catch (error) {
      console.error('Error creating ICP profile:', error);
      throw error;
    }
  }

  async updateICPProfile(id: string, profile: Partial<IdealCustomerProfile>): Promise<IdealCustomerProfile> {
    try {
      const response = await httpClient.put(`/signals/icps/${id}`, profile);
      return response.data;
    } catch (error) {
      console.error('Error updating ICP profile:', error);
      throw error;
    }
  }

  async deleteICPProfile(id: string): Promise<void> {
    try {
      await httpClient.delete(`/signals/icps/${id}`);
    } catch (error) {
      console.error('Error deleting ICP profile:', error);
      throw error;
    }
  }

  // ── Workspace members + invites ──────────────────────────────────────────

  async getWorkspaceMembers(): Promise<WorkspaceMember[]> {
    try {
      const response = await httpClient.get('/workspace/members');
      return response.data || [];
    } catch (error) {
      // Rethrow (not swallow-and-return-[]) so the Members tab can distinguish a real
      // failure from an empty workspace and surface a toast.
      console.error('Error getting workspace members:', error);
      throw error;
    }
  }

  async listWorkspaceInvites(): Promise<WorkspaceInvite[]> {
    try {
      const response = await httpClient.get('/workspace/invites');
      return response.data || [];
    } catch (error) {
      console.error('Error listing workspace invites:', error);
      throw error;
    }
  }

  async createWorkspaceInvite(email: string): Promise<WorkspaceInvite> {
    try {
      const response = await httpClient.post('/workspace/invites', { email });
      return response.data;
    } catch (error) {
      console.error('Error creating workspace invite:', error);
      throw error;
    }
  }

  async revokeWorkspaceInvite(inviteId: string): Promise<void> {
    try {
      await httpClient.delete(`/workspace/invites/${inviteId}`);
    } catch (error) {
      console.error('Error revoking workspace invite:', error);
      throw error;
    }
  }

  async previewWorkspaceInvite(token: string): Promise<InvitePreview> {
    try {
      const response = await httpClient.get(`/workspace/invites/${encodeURIComponent(token)}/preview`);
      return response.data;
    } catch (error) {
      console.error('Error previewing workspace invite:', error);
      throw error;
    }
  }

  async acceptWorkspaceInvite(
    token: string,
    dataChoice: 'bring' | 'fresh' = 'fresh'
  ): Promise<{ workspace_id: string; notice: string | null }> {
    try {
      const response = await httpClient.post('/workspace/invites/accept', {
        token,
        data_choice: dataChoice,
      });
      return response.data;
    } catch (error) {
      console.error('Error accepting workspace invite:', error);
      throw error;
    }
  }

  async removeWorkspaceMember(userId: string): Promise<void> {
    try {
      await httpClient.delete(`/workspace/members/${userId}`);
    } catch (error) {
      console.error('Error removing workspace member:', error);
      throw error;
    }
  }

  async updateWorkspaceMemberRole(userId: string, role: 'owner' | 'member'): Promise<void> {
    try {
      await httpClient.patch(`/workspace/members/${userId}/role`, { role });
    } catch (error) {
      console.error('Error updating workspace member role:', error);
      throw error;
    }
  }

  async leaveWorkspace(): Promise<{ workspace_id: string }> {
    try {
      const response = await httpClient.post('/workspace/leave', {});
      return response.data;
    } catch (error) {
      console.error('Error leaving workspace:', error);
      throw error;
    }
  }

  async getICPMetrics(): Promise<Record<string, { signalCount: number; leadCount: number; lastActivity: string | null }>> {
    try {
      const response = await httpClient.get('/signals/icps/metrics/all');
      // Convert from backend format to frontend format
      const metrics: Record<string, { signalCount: number; leadCount: number; lastActivity: string | null }> = {};
      for (const [id, data] of Object.entries(response.data.metrics || {})) {
        const m = data as { signal_count: number; lead_count: number; last_activity: string | null };
        metrics[id] = {
          signalCount: m.signal_count,
          leadCount: m.lead_count,
          lastActivity: m.last_activity,
        };
      }
      return metrics;
    } catch (error) {
      console.error('Error getting ICP metrics:', error);
      return {};
    }
  }

  // Lead Signals endpoints - Use Supabase
  async getLeadSignals(icpId?: string): Promise<LeadSignal[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from('lead_signals')
        .select('*')
        .eq('user_id', user.id);

      if (icpId) {
        query = query.eq('icp_id', icpId);
      }

      const { data: signals } = await query.order('created_at', { ascending: false });
      return signals || [];
    } catch (error) {
      console.error('Error getting lead signals:', error);
      return [];
    }
  }

  // Website Analysis - Use backend API
  async analyzeWebsite(data: { website_url: string; website_content?: string | null }): Promise<any> {
    try {
      const response = await httpClient.post('/analysis/website', data);
      return response.data;
    } catch (error) {
      // Mock implementation as fallback
      console.warn('Backend not available for website analysis, using mock data:', error);
      return {
        website_accessible: false,
        company_description: "Unable to analyze website at this time.",
        key_products_and_services: ["Professional Services", "Digital Solutions"]
      };
    }
  }

  // Generate Signal Inspirations - Use backend API
  async generateSignalInspirations(icpId: string): Promise<{
    signals: Array<{
      name: string;
      description: string;
      search_criteria: string[];
      target_entity: 'companies' | 'people' | 'custom';
      rationale: string;
      priority: string;
    }>;
    reasoning: string;
    additional_recommendations: string[];
  }> {
    try {
      // Call the signals endpoint which already handles ICP lookup
      const response = await httpClient.post('/signals/generate', {
        icp_id: icpId
      });

      return response.data;
    } catch (error) {
      console.error('Error generating signal inspirations:', error);
      throw error;
    }
  }

  // Signal CRUD endpoints
  async listSignals(icpId?: string): Promise<{
    signals: LeadSignal[];
    total: number;
  }> {
    try {
      const params = icpId ? `?icp_id=${icpId}` : '';
      const response = await httpClient.get(`/signals${params}`);
      return response.data;
    } catch (error) {
      console.error('Error listing signals:', error);
      throw error;
    }
  }

  async createSignal(data: {
    icp_id: string;
    name: string;
    description?: string;
    criteria?: {
      signal_criteria?: string[];
      target_entity?: 'companies' | 'people' | 'custom';
      entity_description?: string;
      source?: string;
    };
  }): Promise<LeadSignal> {
    try {
      const response = await httpClient.post('/signals', data);
      return response.data;
    } catch (error) {
      console.error('Error creating signal:', error);
      throw error;
    }
  }

  async updateSignal(signalId: string, data: {
    name?: string;
    description?: string;
    criteria?: {
      signal_criteria?: string[];
      target_entity?: 'companies' | 'people' | 'custom';
      entity_description?: string;
    };
  }): Promise<LeadSignal> {
    try {
      const response = await httpClient.put(`/signals/${signalId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating signal:', error);
      throw error;
    }
  }

  async deleteSignal(signalId: string): Promise<void> {
    try {
      await httpClient.delete(`/signals/${signalId}`);
    } catch (error) {
      console.error('Error deleting signal:', error);
      throw error;
    }
  }

  // ICP Tables endpoint
  async getICPTables(icpId: string): Promise<{
    tables: LeadTable[];
    total: number;
  }> {
    try {
      const response = await httpClient.get(`/signals/icp/${icpId}/tables`);
      return response.data;
    } catch (error) {
      console.error('Error getting ICP tables:', error);
      throw error;
    }
  }

  // Deploy Signal V2 - Uses consolidated generate-enhanced-v2 endpoint with signal_id
  async deploySignalV2(signalId: string, options: {
    search_query: string;
    criteria?: string[];
    table_type: 'companies' | 'people' | 'custom';
    row_count: number;
    custom_type_description?: string;
    table_name?: string;
  }): Promise<{
    table_id: string;
    table_name: string;
    status: string;
    message: string;
  }> {
    try {
      // Use consolidated endpoint with signal_id parameter
      const response = await httpClient.post('/lead-tables-v2/generate-enhanced-v2', {
        search_query: options.search_query,
        criteria: options.criteria,
        table_type: options.table_type,
        row_count: options.row_count,
        custom_type_description: options.custom_type_description,
        title: options.table_name,
        signal_id: signalId,  // Associate with signal
      });
      return {
        table_id: response.data.table_id,
        table_name: response.data.table_name,
        status: response.data.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Error deploying signal V2:', error);
      throw error;
    }
  }

  // Gmail OAuth endpoints
  async getGmailAuthUrl(): Promise<{ auth_url: string; state: string }> {
    try {
      const response = await httpClient.get('/gmail/auth-url');
      return response.data;
    } catch (error) {
      console.error('Error getting Gmail auth URL:', error);
      throw error;
    }
  }

  async getGmailConnectionStatus(): Promise<{
    is_connected: boolean;
    gmail_email: string | null;
    expires_at: string | null;
    needs_refresh: boolean;
    token_status: string | null;
  }> {
    try {
      const response = await httpClient.get('/gmail/connection-status');
      return response.data;
    } catch (error) {
      console.error('Error getting Gmail connection status:', error);
      throw error;
    }
  }

  async getAllGmailConnections(): Promise<{
    connections: Array<{
      id: string;
      is_connected: boolean;
      gmail_email: string;
      display_name: string;
      is_primary: boolean;
      needs_refresh: boolean;
      token_status: string;
      /** SKY-469: true when the grant lacks gmail.readonly — send works, sync won't. */
      missing_read_scope: boolean;
      created_at: string;
    }>;
  }> {
    try {
      const response = await httpClient.get('/gmail/connections');
      return response.data;
    } catch (error) {
      console.error('Error getting Gmail connections:', error);
      throw error;
    }
  }

  async disconnectGmail(connectionId?: string): Promise<void> {
    try {
      if (connectionId) {
        // Disconnect specific connection
        await httpClient.delete(`/gmail/disconnect/${connectionId}`);
      } else {
        // Disconnect all connections (legacy)
        await httpClient.delete('/gmail/disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting Gmail:', error);
      throw error;
    }
  }

  async refreshGmailToken(): Promise<{ success: boolean; expires_at?: string; error?: string }> {
    try {
      const response = await httpClient.post('/gmail/refresh-token');
      return response.data;
    } catch (error) {
      console.error('Error refreshing Gmail token:', error);
      throw error;
    }
  }

  async setPrimaryGmailConnection(connectionId: string): Promise<any> {
    try {
      const response = await httpClient.post('/gmail/set-primary-email', null, {
        params: { gmail_token_id: connectionId }
      });
      return response.data;
    } catch (error) {
      console.error('Error setting primary Gmail connection:', error);
      throw error;
    }
  }

  async sendGmailEmail(request: {
    recipient: string;
    subject: string;
    body: string;
    cc?: string[];
    bcc?: string[];
    reply_to?: string;
  }): Promise<{
    success: boolean;
    message_id?: string;
    thread_id?: string;
    error?: string;
    log_id?: string;
  }> {
    try {
      const response = await httpClient.post('/gmail/send-email', request);
      return response.data;
    } catch (error) {
      console.error('Error sending Gmail email:', error);
      throw error;
    }
  }

  async getGmailEmailLogs(limit: number = 50, offset: number = 0): Promise<{
    logs: any[];
    total: number;
    limit: number;
    offset: number;
  }> {
    try {
      const response = await httpClient.get('/gmail/email-logs', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting Gmail email logs:', error);
      throw error;
    }
  }

  // Google Calendar API Methods
  async getCalendarAuthUrl(upgradeScope?: boolean): Promise<{ auth_url: string; state: string }> {
    const params = upgradeScope ? '?upgrade_scope=true' : '';
    const response = await httpClient.get(`/google/calendar/auth-url${params}`);
    return response.data;
  }

  async getCalendarConnectionStatus(): Promise<{
    is_connected: boolean;
    email: string | null;
    expires_at: string | null;
    needs_refresh: boolean;
    token_status: string | null;
  }> {
    const response = await httpClient.get('/google/calendar/connection-status');
    return response.data;
  }

  async getAllCalendarConnections(): Promise<{
    connections: Array<{
      id: string;
      is_connected: boolean;
      email: string;
      display_name: string;
      is_primary: boolean;
      needs_refresh: boolean;
      token_status: string;
      has_write_scope: boolean;
      created_at: string;
    }>;
  }> {
    const response = await httpClient.get('/google/calendar/connections');
    return response.data;
  }

  async disconnectCalendar(connectionId: string): Promise<void> {
    await httpClient.delete(`/google/calendar/disconnect/${connectionId}`);
  }

  async setPrimaryCalendarConnection(connectionId: string): Promise<SetPrimaryCalendarResponse> {
    const response = await httpClient.post('/google/calendar/set-primary', null, {
      params: { connection_id: connectionId },
    });
    return response.data;
  }

  async syncCalendar(): Promise<{ synced: number; deleted: number; accounts_synced: number }> {
    const response = await httpClient.post('/google/calendar/sync');
    return response.data;
  }

  async getCalendarEvents(
    daysAhead: number = 7,
    daysBehind: number = 0,
    credentialId?: string,
  ): Promise<CalendarEventsResponse> {
    const params: Record<string, string | number> = { days_ahead: daysAhead };
    if (daysBehind > 0) params.days_behind = daysBehind;
    if (credentialId) params.credential_id = credentialId;
    const response = await httpClient.get('/google/calendar/events', { params });
    return response.data;
  }

  async createCalendarEvent(event: {
    title: string;
    start: string;
    end: string;
    description?: string;
    attendees?: string[];
    credential_id?: string;
  }): Promise<CreatedCalendarEvent> {
    const response = await httpClient.post('/google/calendar/events', event);
    return response.data;
  }

  // LinkedIn API Methods
  async getLinkedInConnectionStatus(): Promise<{
    is_connected: boolean;
    linkedin_email: string | null;
    display_name: string | null;
    expires_at: string | null;
    token_status: string | null;
    credential_id: string | null;
    last_used_at: string | null;
    error_count: number;
  }> {
    try {
      const response = await httpClient.get('/linkedin/credentials/status');
      return response.data;
    } catch (error) {
      console.error('Error getting LinkedIn connection status:', error);
      throw error;
    }
  }

  async getAllLinkedInConnections(): Promise<{
    connections: Array<{
      id: string;
      linkedin_email: string;
      display_name: string | null;
      is_primary: boolean;
      is_active: boolean;
      token_status: string;
      expires_at: string | null;
      last_used_at: string | null;
      error_count: number;
      created_at: string | null;
    }>;
  }> {
    try {
      const response = await httpClient.get('/linkedin/credentials/list');
      return response.data;
    } catch (error) {
      console.error('Error getting LinkedIn connections:', error);
      throw error;
    }
  }

  async disconnectLinkedIn(connectionId?: string): Promise<void> {
    try {
      await httpClient.post('/linkedin/credentials/disconnect', {
        credential_id: connectionId || null
      });
    } catch (error) {
      console.error('Error disconnecting LinkedIn:', error);
      throw error;
    }
  }

  async validateLinkedInToken(credentialId?: string): Promise<{
    valid: boolean;
    error?: string;
    credential_id?: string;
    profile?: any;
  }> {
    try {
      const response = await httpClient.post('/linkedin/credentials/validate', {
        credential_id: credentialId || null
      });
      return response.data;
    } catch (error) {
      console.error('Error validating LinkedIn token:', error);
      throw error;
    }
  }

  async loginLinkedIn(email: string, password: string, country?: string): Promise<{
    status: string;
    message?: string;
    login_token?: string;
    email?: string;
  }> {
    try {
      const response = await httpClient.post('/linkedin/auth/login', {
        email,
        password,
        country: country || 'US'
      });
      return response.data;
    } catch (error) {
      console.error('Error logging in to LinkedIn:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  async verifyLinkedInCode(email: string, code: string, country?: string): Promise<{
    status: string;
    login_token?: string;
    message?: string;
  }> {
    try {
      const response = await httpClient.post('/linkedin/auth/verify-code', {
        email,
        code,
        country: country || 'US'
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying LinkedIn code:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  async updateTableJobStatus(tableId: string, jobId: string | null): Promise<void> {
    try {
      const response = await httpClient.patch(`/lead-tables/${tableId}/job-status`, null, {
        params: { job_id: jobId }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating table job status:', error);
      throw error;
    }
  }

  // Enhanced lead generation endpoints
  async previewDataset(searchQuery: string, tableType: 'companies' | 'people' | 'custom', customTypeDescription?: string): Promise<{
    search: {
      entity: { type: string };
      criteria: Array<{ description: string }>;
    };
    enrichments: Array<{
      description: string;
      format: string;
      options?: Array<{ label: string }>;
    }>;
    status: string;
  }> {
    try {
      const response = await httpClient.post('/lead-tables-v2/preview-dataset', {
        search_query: searchQuery,
        table_type: tableType,
        custom_type_description: customTypeDescription
      });
      return response.data;
    } catch (error) {
      console.error('Error previewing dataset:', error);
      throw error;
    }
  }

  async generateEnhancedLeadTable(searchQuery: string | null, criteria: string[] | null, tableType: string, rowCount: number = 10, customTypeDescription?: string, uploadId?: string | null, title?: string | null): Promise<any> {
    try {
      const response = await httpClient.post('/lead-tables-v2/generate-enhanced-v2', {
        search_query: searchQuery,
        criteria: criteria,
        table_type: tableType,
        row_count: rowCount,
        custom_type_description: customTypeDescription,
        upload_id: uploadId,
        title: title
      });
      return response.data;
    } catch (error: unknown) {
      console.error('Error generating enhanced lead table:', error);
      throw error;
    }
  }

  async findMoreResults(tableId: string, count: number): Promise<{ message: string; status: string; table_id: string }> {
    try {
      const response = await httpClient.post(`/lead-tables-v2/${tableId}/find-more-results`, {
        count: count
      });
      return response.data;
    } catch (error: unknown) {
      console.error('Error finding more results:', error);
      const err = error as { response?: { data?: { detail?: string | { error?: string; message?: string } } }; message?: string };

      // Handle free tier limit error
      if (typeof err?.response?.data?.detail === 'object' && err?.response?.data?.detail?.error === 'free_tier_limit_exceeded') {
        throw new Error(err.response.data.detail.message || 'Free tier limit exceeded');
      }

      // Handle credit error
      if (typeof err?.response?.data?.detail === 'string' && err.response.data.detail.includes(NOT_ENOUGH_CREDITS_ERROR)) {
        throw new Error(OUT_OF_CREDITS_ERROR_MESSAGE);
      }
      throw error;
    }
  }

  async getLeadTable(tableId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/lead-tables/${tableId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting lead table:', error);
      throw error;
    }
  }

  async getTableSegments(tableId: string): Promise<any[]> {
    try {
      const response = await httpClient.get(`/lead-tables/${tableId}/segments`);
      return response.data;
    } catch (error) {
      console.error('Error getting table segments:', error);
      throw error;
    }
  }

  async getLeadRowWithReasoning(leadRowId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/lead-tables/rows/${leadRowId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting lead row with reasoning:', error);
      throw error;
    }
  }

  // Row Enrichment endpoints (legacy)
  async enrichTableRows(tableId: string, request: {
    column_name: string;
    row_ids: string[];
    row_count_limit: number;
  }): Promise<{
    table_id: string;
    column_name: string;
    rows_processed: number;
    rows_enriched: number;
    enrichment_summary: string;
    status: string;
  }> {
    try {
      const response = await httpClient.post(`/lead-tables/${tableId}/enrich`, request);
      return response.data;
    } catch (error) {
      console.error('Error enriching table rows:', error);
      throw error;
    }
  }

  // New Cell Enrichment endpoints
  async enrichTableCells(tableId: string, request: {
    cell_requests: Array<{
      cell_id: string;
      column_name: string;
      row_id: string;
    }>;
  }): Promise<{
    table_id: string;
    cells_queued: number;
    message: string;
    status: string;
  }> {
    try {
      const response = await httpClient.post(`/lead-tables/${tableId}/enrich-cells`, {
        table_id: tableId,
        ...request
      });
      return response.data;
    } catch (error: unknown) {
      console.error('Error enriching table cells:', error);
      const err = error as { response?: { data?: { detail?: string; error?: string } }; message?: string };
      const rawError = err?.response?.data?.detail || err?.response?.data?.error;
      const errorMessage = typeof rawError === 'string' ? rawError : '';
      if (errorMessage.includes(NOT_ENOUGH_CREDITS_ERROR)) {
        throw new Error(OUT_OF_CREDITS_ERROR_MESSAGE);
      }
      throw error;
    }
  }

  // Column-level Batch Enrichment endpoint
  async enrichTableColumn(tableId: string, request: {
    column_name: string;
    row_limit: number | 'all';
    format_hint?: string;
    allow_creative_phrasing?: boolean;
  }): Promise<{
    table_id: string;
    column_name: string;
    rows_queued: number;
    message: string;
    status: string;
  }> {
    try {
      // V2: Use simplified enrichment endpoint (on lead-tables-v2 router)
      const response = await httpClient.post(`/lead-tables-v2/${tableId}/enrich-column-v2`, request);
      return response.data;
    } catch (error: unknown) {
      console.error('Error enriching table column:', error);
      const err = error as { response?: { data?: { detail?: string; error?: string } }; message?: string };
      const rawError = err?.response?.data?.detail || err?.response?.data?.error;
      const errorMessage = typeof rawError === 'string' ? rawError : '';
      if (errorMessage.includes(NOT_ENOUGH_CREDITS_ERROR)) {
        throw new Error(OUT_OF_CREDITS_ERROR_MESSAGE);
      }
      throw error;
    }
  }

  // Atomic Column Creation endpoint
  async ensureTableColumn(tableId: string, columnName: string, columnType: string = 'text'): Promise<{
    success: boolean;
    column: any;
    message: string;
  }> {
    try {
      const response = await httpClient.post(`/lead-tables/${tableId}/columns`, {
        name: columnName,
        column_type: columnType
      });
      return response.data;
    } catch (error) {
      console.error('Error ensuring table column:', error);
      throw error;
    }
  }

  // Email Template endpoints
  async getEmailTemplates(channel?: string): Promise<any> {
    try {
      const params = channel ? `?channel=${encodeURIComponent(channel)}` : '';
      const response = await httpClient.get(`/email-templates/${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching email templates:', error);
      throw error;
    }
  }

  async getEmailTemplate(templateId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/email-templates/${templateId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching email template:', error);
      throw error;
    }
  }

  async createEmailTemplate(templateData: {
    name: string;
    subject: string;
    body: string;
    body_plain?: string;
  }): Promise<any> {
    try {
      const response = await httpClient.post('/email-templates/', templateData);
      return response.data;
    } catch (error) {
      console.error('Error creating email template:', error);
      throw error;
    }
  }

  async updateEmailTemplate(templateId: string, templateData: {
    name?: string;
    subject?: string;
    body?: string;
    body_plain?: string;
    is_active?: boolean;
  }): Promise<any> {
    try {
      const response = await httpClient.put(`/email-templates/${templateId}`, templateData);
      return response.data;
    } catch (error) {
      console.error('Error updating email template:', error);
      throw error;
    }
  }

  async deleteEmailTemplate(templateId: string): Promise<any> {
    try {
      const response = await httpClient.delete(`/email-templates/${templateId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting email template:', error);
      throw error;
    }
  }

  async duplicateEmailTemplate(templateId: string, newName: string): Promise<any> {
    try {
      const response = await httpClient.post(`/email-templates/${templateId}/duplicate`, null, {
        params: { new_name: newName }
      });
      return response.data;
    } catch (error) {
      console.error('Error duplicating email template:', error);
      throw error;
    }
  }

  async sendEmail(emailData: {
    recipient: string;
    subject: string;
    body: string;
    cc?: string[];
    bcc?: string[];
    reply_to?: string;
    variables?: Record<string, string>;
    gmail_connection_id?: string;
  }): Promise<any> {
    try {
      const response = await httpClient.post(`/email-templates/send`, emailData);
      return response.data;
    } catch (error) {
      console.error('Error sending template email:', error);
      throw error;
    }
  }

  async sendTemplateEmail(templateId: string, emailData: {
    recipient: string;
    cc?: string[];
    bcc?: string[];
    reply_to?: string;
    variables?: Record<string, string>;
    gmail_connection_id?: string;
  }): Promise<any> {
    try {
      const response = await httpClient.post(`/email-templates/${templateId}/send`, emailData);
      return response.data;
    } catch (error) {
      console.error('Error sending template email:', error);
      throw error;
    }
  }

  async getEmailTemplateStats(): Promise<any> {
    try {
      const response = await httpClient.get('/email-templates/stats/usage');
      return response.data;
    } catch (error) {
      console.error('Error fetching template stats:', error);
      throw error;
    }
  }

  async getCampaignAnalyticsSummary(): Promise<{
    templates_created: number;
    emails_sent: number;
    successful_sends: number;
    failed_sends: number;
    success_rate: number;
    last_campaign_date: string | null;
  }> {
    try {
      const response = await httpClient.get('/campaign-analytics/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign analytics:', error);
      throw error;
    }
  }

  // === DATASET SHARING ENDPOINTS ===

  async createShareLink(tableId: string, settings: {
    title?: string;
    description?: string;
    expires_at?: string;
    max_access_count?: number;
  }): Promise<{
    share_token: string;
    share_url: string;
    shared_dataset: any;
  }> {
    try {
      const response = await httpClient.post(`/sharing/tables/${tableId}/share`, settings);

      // Add full domain to share URL
      const result = response.data;
      result.share_url = `${window.location.origin}${result.share_url}`;

      return result;
    } catch (error) {
      console.error('Error creating share link:', error);
      throw error;
    }
  }

  async getSharedDataset(shareToken: string): Promise<{
    dataset: any;
    leads: any[];
    columns: string[];
  } | null> {
    try {
      const response = await httpClient.get(`/sharing/shared/${shareToken}`);
      const data = response.data;

      // Transform reasoning keys: Backend returns reasoning keyed by raw criterion_text,
      // but frontend expects hashed keys to match segment IDs
      if (data?.leads) {
        data.leads = data.leads.map((lead: any) => {
          if (lead.reasoning) {
            const transformedReasoning: Record<string, any> = {};

            // Re-key the reasoning object using hashed criterion_text
            Object.entries(lead.reasoning).forEach(([_originalKey, reasoningData]: [string, any]) => {
              const criterionText = reasoningData.criterion_text;
              if (criterionText) {
                // Hash the criterion_text to match frontend segment IDs
                const hashedKey = hashCriterionTextSync(criterionText);
                transformedReasoning[hashedKey] = reasoningData;
              } else {
                // Fallback for items without criterion_text (shouldn't happen)
                transformedReasoning[_originalKey] = reasoningData;
              }
            });

            return {
              ...lead,
              reasoning: transformedReasoning
            };
          }
          return lead;
        });
      }

      return data;
    } catch (error: unknown) {
      console.error('Error getting shared dataset:', error);

      // Handle specific HTTP status codes for expired/not found shares
      const err = error as { response?: { status?: number } };
      if (err?.response?.status === 404 || err?.response?.status === 410) {
        return null;
      }

      throw error;
    }
  }

  async updateSharedDataset(shareToken: string, updates: {
    title?: string;
    description?: string;
    is_active?: boolean;
    expires_at?: string;
    max_access_count?: number;
  }): Promise<any> {
    try {
      const response = await httpClient.put(`/sharing/shared/${shareToken}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating shared dataset:', error);
      throw error;
    }
  }

  async deleteSharedDataset(shareToken: string): Promise<void> {
    try {
      await httpClient.delete(`/sharing/shared/${shareToken}`);
    } catch (error) {
      console.error('Error deleting shared dataset:', error);
      throw error;
    }
  }

  async getDatasetShares(tableId: string): Promise<any[]> {
    try {
      const response = await httpClient.get(`/sharing/tables/${tableId}/shares`);
      return response.data || [];
    } catch (error) {
      console.error('Error getting dataset shares:', error);
      return [];
    }
  }

  async getShareAnalytics(shareToken: string): Promise<{
    total_views: number;
    recent_views: any[];
    share_info: any;
  }> {
    try {
      const response = await httpClient.get(`/sharing/shared/${shareToken}/analytics`);
      return response.data;
    } catch (error) {
      console.error('Error getting share analytics:', error);
      throw error;
    }
  }

  // ── Meeting Sharing ─────────────────────────────────────────────────────

  async shareMeeting(meetingId: string, sharerName: string, options?: {
    expires_at?: string;
    max_access_count?: number;
  }): Promise<{
    share_token: string;
    share_url: string;
    shared_meeting: Record<string, unknown>;
  }> {
    try {
      const response = await httpClient.post(`/sharing/meetings/${meetingId}/share`, {
        sharer_name: sharerName,
        ...options,
      });
      const result = response.data;
      result.share_url = `${window.location.origin}${result.share_url}`;
      return result;
    } catch (error) {
      console.error('Error creating meeting share link:', error);
      throw error;
    }
  }

  async getSharedMeeting(shareToken: string): Promise<{
    shared_meeting: {
      share_token: string;
      title: string | null;
      access_count: number;
      max_access_count: number | null;
      expires_at: string | null;
      created_at?: string;
    };
    meeting: { title: string | null; category: string | null; created_at: string };
    ai_summary: { title: string; overview: string; sections: { title: string; items: string[] }[] } | null;
    sharer_name: string | null;
  } | null> {
    try {
      const response = await httpClient.get(`/sharing/shared-meeting/${shareToken}`);
      return response.data;
    } catch (error: unknown) {
      console.error('Error getting shared meeting:', error);
      const err = error as { response?: { status?: number; data?: { detail?: string } } };
      if (err?.response?.status === 404) {
        return null;
      }
      if (err?.response?.status === 410) {
        const detail = err.response.data?.detail || '';
        const isExpired = detail.toLowerCase().includes('expired');
        const errorType = isExpired ? 'expired' : 'limit_exceeded';
        throw Object.assign(new Error(detail), { type: errorType });
      }
      throw error;
    }
  }

  async deleteSharedMeeting(shareToken: string): Promise<void> {
    try {
      await httpClient.delete(`/sharing/shared-meeting/${shareToken}`);
    } catch (error) {
      console.error('Error deleting shared meeting:', error);
      throw error;
    }
  }

  async createStripeCustomer(displayName: string): Promise<any> {
    try {
      const response = await httpClient.post(`/billing/create-stripe-customer`, { user_display_name: displayName });
      return response.data;
    } catch (error) {
      console.error('Error creating stripe customer:', error);
      throw error;
    }
  }

  async getSubscriptionStatus(): Promise<any> {
    try {
      const response = await httpClient.get(`/billing/get-subscription-status`);
      return response.data;
    } catch (error) {
      console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  async getCredits(): Promise<any> {
    try {
      const response = await httpClient.get(`/billing/get-credits`);
      return response.data;
    } catch (error) {
      console.error('Error getting credits:', error);
      throw error;
    }
  }

  async logUserAction(actionData: UserActionRequest): Promise<UserActionResponse> {
    try {
      const response = await httpClient.post<UserActionResponse>('/analytics/log-action', actionData);
      return response.data;
    } catch (error) {
      console.error('Error logging user action:', error);
      throw error;
    }
  }

  async getUserAnalytics(limit: number = 3, userId?: string): Promise<any> {
    try {
      let url = `/analytics/sessions?limit=${limit}`;
      if (userId) {
        url += `&user_id=${userId}`;
      }
      const response = await httpClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      throw error;
    }
  }

  async uploadDatasetCSV(file: File, title: string, entity: string, entity_description: string, count: number, identifier_idx: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('entity', entity);
      if (entity_description) {
        formData.append('entity_description', entity_description);
      }
      formData.append('count', count.toString());
      formData.append('identifier_idx', identifier_idx.toString());

      const response = await httpClient.post(`/lead-tables-v2/upload-dataset-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading dataset CSV:', error);
      throw error;
    }
  }

  // Admin endpoints
  async getDailyMetrics(days: number = 30): Promise<any> {
    try {
      const response = await httpClient.get(`/admin/metrics/daily?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching daily metrics:', error);
      throw error;
    }
  }

  async getAdminUsersList(page = 0, perPage = 20, search = ''): Promise<any> {
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (search) params.set('search', search);
      const response = await httpClient.get(`/admin/users?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin users list:', error);
      throw error;
    }
  }

  async getUserDetails(userId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }

  async getClientEntities(): Promise<ClientEntityRow[]> {
    try {
      const response = await httpClient.get('/admin/accounts/clients');
      return response.data;
    } catch (error) {
      console.error('Error fetching client entities:', error);
      throw error;
    }
  }

  async getAdminAccounts(): Promise<AccountListRow[]> {
    try {
      const response = await httpClient.get('/admin/accounts');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin accounts:', error);
      throw error;
    }
  }

  async getAdminAccountDetail(entityId: string): Promise<AccountDetail> {
    try {
      const response = await httpClient.get(`/admin/accounts/${entityId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin account detail:', error);
      throw error;
    }
  }

  // ── Slack agent: channel → organization mapping (admin) ─────────────────────

  async getSlackStatus(): Promise<SlackStatus> {
    try {
      const response = await httpClient.get('/slack/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching slack status:', error);
      throw error;
    }
  }

  async getSlackChannels(): Promise<SlackChannelOption[]> {
    try {
      const response = await httpClient.get('/slack/channels');
      return response.data?.channels ?? [];
    } catch (error) {
      console.error('Error fetching slack channels:', error);
      throw error;
    }
  }


  async listSlackChannelMap(): Promise<SlackChannelMapping[]> {
    try {
      const response = await httpClient.get('/slack/agent/channel-map');
      return response.data;
    } catch (error) {
      console.error('Error fetching slack channel map:', error);
      throw error;
    }
  }

  async upsertSlackChannelMap(body: {
    slack_team_id: string;
    channel_id: string;
    workspace_id: string;
    channel_name?: string | null;
  }): Promise<SlackChannelMapping> {
    try {
      const response = await httpClient.put('/slack/agent/channel-map', body);
      return response.data;
    } catch (error) {
      console.error('Error upserting slack channel map:', error);
      throw error;
    }
  }

  async deleteSlackChannelMap(slackTeamId: string, channelId: string): Promise<void> {
    try {
      await httpClient.delete(`/slack/agent/channel-map/${slackTeamId}/${channelId}`);
    } catch (error) {
      console.error('Error deleting slack channel map:', error);
      throw error;
    }
  }

  async getAdminOrganizations(): Promise<AdminOrganization[]> {
    try {
      const response = await httpClient.get('/admin/organizations');
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw error;
    }
  }

  // ── Admin: user → workspace mapping ──────────────────────────────────────────

  async listAdminUserWorkspaceMappings(): Promise<AdminUserWorkspaceMapping[]> {
    try {
      const response = await httpClient.get('/admin/workspace-members');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin workspace mappings:', error);
      throw error;
    }
  }

  async upsertAdminUserWorkspaceMapping(body: {
    user_id: string;
    workspace_id: string;
    role: 'owner' | 'member';
  }): Promise<AdminUserWorkspaceMapping> {
    try {
      const response = await httpClient.put('/admin/workspace-members', body);
      return response.data;
    } catch (error) {
      console.error('Error upserting admin workspace mapping:', error);
      throw error;
    }
  }

  async deleteAdminUserWorkspaceMapping(workspaceId: string, userId: string): Promise<void> {
    try {
      await httpClient.delete(`/admin/workspace-members/${workspaceId}/${userId}`);
    } catch (error) {
      console.error('Error deleting admin workspace mapping:', error);
      throw error;
    }
  }

  // ── Admin: signups awaiting approval ─────────────────────────────────────────

  async listAdminSignups(limit: number, offset: number): Promise<AdminSignupsPage> {
    try {
      const response = await httpClient.get('/admin/signups', { params: { limit, offset } });
      return { items: response.data?.items ?? [], total: response.data?.total ?? 0 };
    } catch (error) {
      console.error('Error fetching admin signups:', error);
      throw error;
    }
  }

  async approveAdminSignup(userId: string, body: { workspace_name?: string }): Promise<AdminSignupApproval> {
    try {
      const response = await httpClient.post(`/admin/signups/${encodeURIComponent(userId)}/approve`, body);
      return response.data;
    } catch (error) {
      console.error('Error approving signup:', error);
      throw error;
    }
  }

  // ── Client cockpit: workspace members, project meta, brief, timeline ────────

  async getAdminWorkspaceMembers(): Promise<AdminWorkspaceMember[]> {
    try {
      const response = await httpClient.get('/admin/accounts/members');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching admin workspace members:', error);
      throw error;
    }
  }

  async updateProjectMeta(
    projectId: string,
    body: { assigned_to?: string | null; tags?: string[]; instantly_campaign_id?: string | null }
  ): Promise<Record<string, unknown>> {
    try {
      const response = await httpClient.patch(`/admin/accounts/projects/${projectId}`, body);
      return response.data;
    } catch (error) {
      console.error('Error updating project meta:', error);
      throw error;
    }
  }

  async getEntityBrief(entityId: string): Promise<EntityBrief> {
    try {
      const response = await httpClient.get(`/accounts/entities/${entityId}/brief`);
      return response.data;
    } catch (error) {
      console.error('Error fetching entity brief:', error);
      throw error;
    }
  }

  async getEntityCampaignHistory(entityId: string): Promise<CampaignHistoryItem[]> {
    try {
      const response = await httpClient.get(`/accounts/entities/${entityId}/campaign-history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching entity campaign history:', error);
      throw error;
    }
  }

  async getEntityTimeline(entityId: string, limit = 15): Promise<TimelineResponse> {
    try {
      const response = await httpClient.get(
        `/accounts/entities/${entityId}/timeline?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching entity timeline:', error);
      throw error;
    }
  }

  async getAdminCampaigns(): Promise<CampaignsOverview> {
    try {
      const response = await httpClient.get('/admin/campaigns');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin campaigns:', error);
      throw error;
    }
  }

  async getAdminAgentUsage(days: number): Promise<AgentUsageRollup> {
    try {
      const response = await httpClient.get(`/admin/agent-usage?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin agent usage:', error);
      throw error;
    }
  }

  async getAdminMargin(query: MarginQuery): Promise<MarginRollup> {
    try {
      const response = await httpClient.get('/admin/agent-usage/margin', {
        params: {
          days: query.days,
          workspace_id: query.workspaceId ?? undefined,
          sort: query.sort,
          desc: query.desc,
          limit: query.limit,
          offset: query.offset,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin margin:', error);
      throw error;
    }
  }

  async getStandup(): Promise<StandupDigest> {
    try {
      const response = await httpClient.get('/admin/standup');
      return response.data;
    } catch (error) {
      console.error('Error fetching standup digest:', error);
      throw error;
    }
  }

  async getAdminReplies(unreadOnly = true): Promise<ReplyItem[]> {
    try {
      const response = await httpClient.get(`/admin/replies?unread_only=${unreadOnly}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch admin replies:', error);
      throw error;
    }
  }

  async markReplyRead(replyKey: string): Promise<{ reply_key: string; read: boolean }> {
    try {
      const response = await httpClient.post(`/admin/replies/${encodeURIComponent(replyKey)}/read`);
      return response.data;
    } catch (error) {
      console.error('Failed to mark reply read:', error);
      throw error;
    }
  }

  async getDatasetStatus(tableId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/lead-tables-v2/${tableId}/get-dataset-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dataset status:', error);
      throw error;
    }
  }

  // Temp solution until webhooks are implemented for automatic data flow from DB
  async getInterimItems(tableId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/lead-tables-v2/${tableId}/interim-items`);
      return response.data;
    } catch (error) {
      console.debug('Error fetching interim items (non-critical):', error);
      throw error;
    }
  }

  // Workflow endpoints
  async generateWorkflowFromDescription(description: string, datasetId: string, aiService: string = 'openai'): Promise<any> {
    try {
      const response = await httpClient.post('/workflows/generate-from-description', {
        description,
        dataset_id: datasetId,
        ai_service: aiService
      });
      return response.data;
    } catch (error) {
      console.error('Error generating workflow from description:', error);
      throw error;
    }
  }

  async getWorkflows(): Promise<any> {
    try {
      const response = await httpClient.get('/workflows/');
      return response.data;
    } catch (error) {
      console.error('Error fetching workflows:', error);
      throw error;
    }
  }

  async getWorkflow(workflowId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/workflows/${workflowId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow:', error);
      throw error;
    }
  }

  async createWorkflow(workflowData: {
    name: string;
    trigger_type: string;
    trigger_config: any;
    graph: any;
    is_active: boolean;
  }): Promise<any> {
    try {
      const response = await httpClient.post('/workflows/', workflowData);
      return response.data;
    } catch (error) {
      console.error('Error creating workflow:', error);
      throw error;
    }
  }

  async updateWorkflow(workflowId: string, workflowData: {
    name?: string;
    trigger_type?: string;
    trigger_config?: any;
    graph?: any;
    is_active?: boolean;
  }): Promise<any> {
    try {
      const response = await httpClient.put(`/workflows/${workflowId}`, workflowData);
      return response.data;
    } catch (error) {
      console.error('Error updating workflow:', error);
      throw error;
    }
  }

  async publishWorkflow(workflowId: string): Promise<any> {
    try {
      const response = await httpClient.post(`/workflows/${workflowId}/publish`);
      return response.data;
    } catch (error) {
      console.error('Error publishing workflow:', error);
      throw error;
    }
  }

  async unpublishWorkflow(workflowId: string): Promise<any> {
    try {
      const response = await httpClient.post(`/workflows/${workflowId}/unpublish`);
      return response.data;
    } catch (error) {
      console.error('Error unpublishing workflow:', error);
      throw error;
    }
  }

  async archiveWorkflow(workflowId: string): Promise<any> {
    try {
      const response = await httpClient.post(`/workflows/${workflowId}/archive`);
      return response.data;
    } catch (error) {
      console.error('Error archiving workflow:', error);
      throw error;
    }
  }

  async executeWorkflow(workflowId: string): Promise<any> {
    try {
      const response = await httpClient.post('/workflows/execute', {
        workflow_id: workflowId
      });
      return response.data;
    } catch (error) {
      console.error('Error executing workflow:', error);
      throw error;
    }
  }

  async getWorkflowRuns(workflowId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/workflows/${workflowId}/runs`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow runs:', error);
      throw error;
    }
  }

  async getWorkflowStats(workflowId: string): Promise<any> {
    try {
      const response = await httpClient.get(`/workflows/${workflowId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow stats:', error);
      throw error;
    }
  }

  // Welcome email endpoint
  async sendWelcomeEmail(): Promise<{ success: boolean; message: string; sent: boolean }> {
    try {
      const response = await httpClient.post('/emails/send_welcome_email');
      return response.data;
    } catch (error) {
      console.error('Error sending welcome email:', error);
      throw error;
    }
  }

  // Emails sent notification endpoint
  async notifyEmailsSent(data: {
    dataset_id: string;
    emails_sent: number;
    emails_failed?: number;
  }): Promise<{ success: boolean; message: string; sent: boolean }> {
    try {
      const response = await httpClient.post('/emails/notify_emails_sent', data);
      return response.data;
    } catch (error) {
      console.error('Error sending emails sent notification:', error);
      // Don't throw - notification failures shouldn't break the flow
      return { success: false, message: 'Notification failed', sent: false };
    }
  }

  // === MONITORING ENDPOINTS ===

  async listMonitors(tableId?: string): Promise<{
    monitors: import('../types/monitoring').Monitor[];
    count: number;
  }> {
    try {
      const params = tableId ? { table_id: tableId } : {};
      const response = await httpClient.get('/monitors/', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing monitors:', error);
      throw error;
    }
  }

  async getMonitor(monitorId: string): Promise<import('../types/monitoring').Monitor> {
    try {
      const response = await httpClient.get(`/monitors/${monitorId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting monitor:', error);
      throw error;
    }
  }

  async createMonitor(data: import('../types/monitoring').MonitorCreateRequest): Promise<import('../types/monitoring').Monitor> {
    try {
      const response = await httpClient.post('/monitors/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating monitor:', error);
      throw error;
    }
  }

  async createMonitorWithDataset(
    data: import('../types/monitoring').MonitorWithDatasetRequest
  ): Promise<import('../types/monitoring').MonitorWithDatasetResponse> {
    try {
      const response = await httpClient.post('/monitors/create-with-dataset', data);
      return response.data;
    } catch (error) {
      console.error('Error creating monitor with dataset:', error);
      throw error;
    }
  }

  async updateMonitor(
    monitorId: string,
    data: import('../types/monitoring').MonitorUpdateRequest
  ): Promise<import('../types/monitoring').Monitor> {
    try {
      const response = await httpClient.patch(`/monitors/${monitorId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating monitor:', error);
      throw error;
    }
  }

  async deleteMonitor(monitorId: string): Promise<void> {
    try {
      await httpClient.delete(`/monitors/${monitorId}`);
    } catch (error) {
      console.error('Error deleting monitor:', error);
      throw error;
    }
  }

  async listMonitorRuns(
    monitorId: string,
    limit: number = 20
  ): Promise<{
    runs: import('../types/monitoring').MonitorRun[];
    count: number;
  }> {
    try {
      const response = await httpClient.get(`/monitors/${monitorId}/runs`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error listing monitor runs:', error);
      throw error;
    }
  }

  async getWorkflowSchema(): Promise<any> {
    try {
      const response = await httpClient.get('/workflows/schema');
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow schema:', error);
      throw error;
    }
  }

  /**
   * Send a chat message to the backend.
   * @param message - The message content
   * @param context - Context for the conversation
   * @param messageHistory - Previous messages in the conversation
   * @param sessionId - Optional session ID to continue a conversation
   * @param saveOnly - If true, saves messages without LLM processing (for quick action flows)
   */
  async sendChatMessage(
    message: string,
    context: any,
    messageHistory: Array<{role: 'user' | 'assistant', content: string}>,
    sessionId: string | null,
    saveOnly: boolean = false
  ): Promise<ChatResponse> {
    try {
      const response = await httpClient.post('/chat/message', {
        message,
        session_id: sessionId,
        context,
        message_history: messageHistory,
        save_only: saveOnly
      });
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

  // Chat session management methods

  async linkChatToDataset(leadTableId: string, sessionId: string) {
    try {
      const response = await httpClient.post('/chat/sessions/link-to-dataset', {
        session_id: sessionId,
        lead_table_id: leadTableId
      });
      return response.data;
    } catch (error) {
      console.error('Error linking chat to dataset:', error);
      throw error;
    }
  }

  async getChatSessionByDataset(leadTableId: string) {
    try {
      const response = await httpClient.get(`/chat/sessions/dataset/${leadTableId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting chat session by dataset:', error);
      throw error;
    }
  }

  // Feature Flags
  async getFeatureFlags(): Promise<{ name: string; enabled: boolean }[]> {
    try {
      const response = await httpClient.get('/feature-flags/');
      return response.data;
    } catch (error) {
      console.error('Error getting feature flags:', error);
      throw error;
    }
  }

  // Notification Preferences
  async getNotificationPreferences(): Promise<NotificationPreferencesResponse> {
    try {
      const response = await httpClient.get('/notification-preferences/');
      return response.data;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      throw error;
    }
  }

  async updateNotificationPreferences(
    preferences: NotificationPreferenceUpdate[]
  ): Promise<NotificationPreferencesResponse> {
    try {
      const response = await httpClient.put('/notification-preferences/', {
        preferences
      });
      return response.data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  // =============================================================================
  // CAMPAIGNS API
  // =============================================================================

  async createCampaign(data: {
    name: string;
    description?: string;
    channel?: string;
    credential_id?: string;
    email_template_id?: string;
    email_subject?: string;
    email_body?: string;
    timezone?: string;
    send_window_start?: string;
    send_window_end?: string;
    send_days?: string[];
    daily_limit?: number;
    dataset_ids?: string[];
  }) {
    try {
      const response = await httpClient.post('/campaigns/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  async listCampaigns(params?: { status?: string; limit?: number; offset?: number }) {
    try {
      const response = await httpClient.get('/campaigns/', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing campaigns:', error);
      throw error;
    }
  }

  async getCampaign(campaignId: string) {
    try {
      const response = await httpClient.get(`/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting campaign:', error);
      throw error;
    }
  }

  async updateCampaign(campaignId: string, data: {
    name?: string;
    description?: string;
    status?: string;
    channel?: string;
    credential_id?: string;
    email_template_id?: string;
    email_subject?: string;
    email_body?: string;
    timezone?: string;
    send_window_start?: string;
    send_window_end?: string;
    send_days?: string[];
    daily_limit?: number;
  }) {
    try {
      const response = await httpClient.patch(`/campaigns/${campaignId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  }

  async deleteCampaign(campaignId: string) {
    try {
      const response = await httpClient.delete(`/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw error;
    }
  }

  async changeCampaignStatus(campaignId: string, newStatus: string) {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/status`, null, {
        params: { new_status: newStatus }
      });
      return response.data;
    } catch (error) {
      console.error('Error changing campaign status:', error);
      throw error;
    }
  }

  async getCampaignStats(campaignId: string) {
    try {
      const response = await httpClient.get(`/campaigns/${campaignId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error getting campaign stats:', error);
      throw error;
    }
  }

  // Campaign Datasets
  async addDatasetToCampaign(campaignId: string, leadTableId: string) {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/datasets`, {
        lead_table_id: leadTableId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding dataset to campaign:', error);
      throw error;
    }
  }

  async listCampaignDatasets(campaignId: string) {
    try {
      const response = await httpClient.get(`/campaigns/${campaignId}/datasets`);
      return response.data;
    } catch (error) {
      console.error('Error listing campaign datasets:', error);
      throw error;
    }
  }

  async removeDatasetFromCampaign(campaignId: string, leadTableId: string, removeRecipients = false) {
    try {
      await httpClient.delete(`/campaigns/${campaignId}/datasets/${leadTableId}`, {
        params: { remove_recipients: removeRecipients }
      });
    } catch (error) {
      console.error('Error removing dataset from campaign:', error);
      throw error;
    }
  }

  // Campaign Workflow Integration
  async activateCampaign(campaignId: string, steps?: Array<{
    step_number: number;
    template_id?: string;
    subject?: string;
    body?: string;
    body_plain?: string;
    delay_days?: number;
    delay_hours?: number;
    send_if_no_reply?: boolean;
  }>) {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/activate`, { steps });
      return response.data;
    } catch (error) {
      console.error('Error activating campaign:', error);
      throw error;
    }
  }

  async pauseCampaign(campaignId: string) {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/pause`);
      return response.data;
    } catch (error) {
      console.error('Error pausing campaign:', error);
      throw error;
    }
  }

  async getCampaignWorkflow(campaignId: string) {
    try {
      const response = await httpClient.get(`/campaigns/${campaignId}/workflow`);
      return response.data;
    } catch (error) {
      console.error('Error getting campaign workflow:', error);
      throw error;
    }
  }

  async detachCampaignWorkflow(campaignId: string) {
    try {
      await httpClient.post(`/campaigns/${campaignId}/workflow/detach`);
    } catch (error) {
      console.error('Error detaching campaign workflow:', error);
      throw error;
    }
  }

  // Campaign Execution
  async executeCampaign(
    campaignId: string,
    options?: { recipientIds?: string[]; maxSends?: number }
  ): Promise<{
    success: boolean;
    campaign_id: string;
    total: number;
    sent: number;
    failed: number;
    failures: Array<{ recipient_id: string; email?: string; error: string }>;
    message?: string;
  }> {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/execute`, {
        recipient_ids: options?.recipientIds ?? null,
        max_sends: options?.maxSends ?? null,
      });
      return response.data;
    } catch (error) {
      console.error('Error executing campaign:', error);
      throw error;
    }
  }

  // Campaign Recipients
  async enrollRecipients(
    campaignId: string,
    leadRowIds: string[],
    options?: { metadata?: Record<string, unknown>; emailColumn?: string }
  ) {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/recipients/enroll`, {
        lead_row_ids: leadRowIds,
        metadata: options?.metadata,
        email_column: options?.emailColumn,
      });
      return response.data;
    } catch (error) {
      console.error('Error enrolling recipients:', error);
      throw error;
    }
  }

  async enrollFromDataset(
    campaignId: string,
    leadTableId: string,
    options?: { limit?: number; filterCriteria?: Record<string, unknown>; emailColumn?: string }
  ) {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/recipients/enroll-from-dataset`, {
        lead_table_id: leadTableId,
        limit: options?.limit ?? null,
        filter_criteria: options?.filterCriteria,
        email_column: options?.emailColumn,
      });
      return response.data;
    } catch (error) {
      console.error('Error enrolling from dataset:', error);
      throw error;
    }
  }

  async listCampaignRecipients(campaignId: string, params?: { status?: string; search?: string; limit?: number; offset?: number }) {
    try {
      const response = await httpClient.get(`/campaigns/${campaignId}/recipients`, { params });
      return response.data;
    } catch (error) {
      console.error('Error listing campaign recipients:', error);
      throw error;
    }
  }

  async batchRemoveRecipients(campaignId: string, recipientIds: string[]): Promise<{ success: boolean; removed_count: number; message: string }> {
    try {
      const response = await httpClient.post(`/campaigns/${campaignId}/recipients/batch-remove`, {
        recipient_ids: recipientIds
      });
      return response.data;
    } catch (error) {
      console.error('Error batch removing recipients:', error);
      throw error;
    }
  }

  async updateRecipientStatus(campaignId: string, recipientId: string, status: string) {
    try {
      const response = await httpClient.patch(`/campaigns/${campaignId}/recipients/${recipientId}`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating recipient status:', error);
      throw error;
    }
  }

  async removeRecipient(campaignId: string, recipientId: string) {
    try {
      await httpClient.delete(`/campaigns/${campaignId}/recipients/${recipientId}`);
    } catch (error) {
      console.error('Error removing recipient:', error);
      throw error;
    }
  }

  // === AFFILIATE PARTNER PROGRAM ENDPOINTS ===

  /**
   * Validate a partner code (public endpoint - no auth required)
   */
  async validatePartnerCode(code: string): Promise<{
    valid: boolean;
    partner_code: string;
    partner_name: string | null;
    message: string | null;
  }> {
    const response = await httpClient.post('/affiliates/validate-code', {
      partner_code: code
    });
    return response.data;
  }

  /**
   * Record a referral for the authenticated user
   */
  async recordReferral(data: {
    partner_code: string;
    attribution_source: 'url' | 'manual_code';
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    landing_page?: string;
    referrer_url?: string;
  }): Promise<{
    success: boolean;
    referral_id: string | null;
    partner_name: string | null;
    message: string;
  }> {
    const response = await httpClient.post('/affiliates/record-referral', data);
    return response.data;
  }

  /**
   * Get the current user's referral information
   */
  async getMyReferral(): Promise<{
    has_referral: boolean;
    partner_code?: string;
    partner_name?: string;
    attribution_source?: string;
    conversion_status?: string;
    signed_up_at?: string;
    onboarding_completed_at?: string;
    first_subscription_at?: string;
  }> {
    const response = await httpClient.get('/affiliates/my-referral');
    return response.data;
  }

  async notifyOnboardingComplete(): Promise<{ success: boolean }> {
    try {
      const response = await httpClient.post('/affiliates/notify-onboarding-complete');
      return response.data;
    } catch {
      // Non-critical — don't block onboarding if affiliate tracking fails
      return { success: false };
    }
  }

  // === AFFILIATE PARTNER SELF-SERVICE ENDPOINTS ===

  async getMyPartnerDashboard(): Promise<{
    is_partner: boolean;
    partner_id?: string;
    partner_code?: string;
    partner_name?: string;
    status?: string;
    tier?: string;
    total_referrals: number;
    signups: number;
    onboarded: number;
    subscribed: number;
    conversion_rate_signup_to_onboard: number;
    conversion_rate_onboard_to_subscribe: number;
  }> {
    const response = await httpClient.get('/affiliates/partner/me');
    return response.data;
  }

  async getMyPartnerReferrals(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    referrals: Array<{
      id: string;
      redacted_email?: string;
      attribution_source: string;
      conversion_status: string;
      signed_up_at: string;
      onboarding_completed_at?: string;
      first_subscription_at?: string;
    }>;
    total: number;
  }> {
    const response = await httpClient.get('/affiliates/partner/referrals', { params });
    return response.data;
  }

  async searchUsersForPartnerLinking(email: string): Promise<{
    users: Array<{
      user_id: string;
      email: string;
      full_name?: string;
      created_at?: string;
      already_linked: boolean;
    }>;
  }> {
    const response = await httpClient.get('/affiliates/admin/search-users', {
      params: { email },
    });
    return response.data;
  }

  // === AFFILIATE ADMIN ENDPOINTS ===

  async getAffiliateDashboardStats(): Promise<{
    total_partners: number;
    active_partners: number;
    total_referrals: number;
    total_signups: number;
    total_onboarded: number;
    total_subscribed: number;
    conversion_rate_signup_to_onboard: number;
    conversion_rate_onboard_to_subscribe: number;
    total_revenue: number;
    total_commission: number;
    recent_signups_7d: number;
    recent_subscriptions_7d: number;
  }> {
    const response = await httpClient.get('/affiliates/admin/dashboard');
    return response.data;
  }

  async listAffiliatePartners(params?: {
    status?: 'active' | 'inactive' | 'suspended';
    limit?: number;
    offset?: number;
  }): Promise<{
    partners: Array<{
      id: string;
      partner_code: string;
      partner_name: string;
      company_name?: string;
      email: string;
      commission_rate: number;
      status: string;
      tier: string;
      metadata: Record<string, unknown>;
      created_at: string;
      updated_at: string;
      total_referrals: number;
      signups: number;
      onboarded: number;
      subscribed: number;
      total_revenue: number;
      total_commission: number;
    }>;
    total: number;
  }> {
    const response = await httpClient.get('/affiliates/admin/partners', { params });
    return response.data;
  }

  async createAffiliatePartner(data: {
    partner_code: string;
    partner_name: string;
    company_name?: string;
    email: string;
    commission_rate?: number;
    tier?: string;
    user_id?: string;
  }): Promise<{
    id: string;
    partner_code: string;
    partner_name: string;
  }> {
    const response = await httpClient.post('/affiliates/admin/partners', data);
    return response.data;
  }

  async updateAffiliatePartner(partnerId: string, data: {
    partner_name?: string;
    company_name?: string;
    email?: string;
    commission_rate?: number;
    status?: 'active' | 'inactive' | 'suspended';
    tier?: string;
    user_id?: string | null;
  }): Promise<{
    id: string;
    partner_code: string;
    partner_name: string;
    status: string;
  }> {
    const response = await httpClient.patch(`/affiliates/admin/partners/${partnerId}`, data);
    return response.data;
  }

  async listAffiliateReferrals(params?: {
    partner_id?: string;
    conversion_status?: 'signup' | 'onboarded' | 'subscribed';
    limit?: number;
    offset?: number;
  }): Promise<{
    referrals: Array<{
      id: string;
      partner_id: string;
      partner_code: string;
      partner_name: string;
      user_id: string;
      user_email?: string;
      attribution_source: string;
      conversion_status: string;
      signed_up_at: string;
      onboarding_completed_at?: string;
      first_subscription_at?: string;
      total_revenue_generated: number;
      total_commission_earned: number;
    }>;
    total: number;
  }> {
    const response = await httpClient.get('/affiliates/admin/referrals', { params });
    return response.data;
  }

  async listAffiliateEvents(params?: {
    partner_id?: string;
    event_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    events: Array<{
      id: string;
      referral_id: string;
      partner_id: string;
      partner_code?: string;
      user_id?: string;
      user_email?: string;
      event_type: string;
      revenue_amount?: number;
      commission_amount?: number;
      event_data: Record<string, unknown>;
      created_at: string;
    }>;
    total: number;
  }> {
    const response = await httpClient.get('/affiliates/admin/events', { params });
    return response.data;
  }

  // --- Mailbox pool (SKY-458, admin-only endpoints) ---

  async getMailboxPool(): Promise<MailboxRow[]> {
    const response = await httpClient.get('/mailboxes');
    return response.data;
  }

  async getAssignableCampaigns(): Promise<AssignableCampaign[]> {
    const response = await httpClient.get('/mailboxes/campaigns');
    return response.data;
  }

  async syncMailboxPool(): Promise<MailboxSyncResult> {
    const response = await httpClient.post('/mailboxes/sync');
    return response.data;
  }

  async assignMailbox(emailAddress: string, updates: MailboxAssignUpdates): Promise<MailboxAssignResult> {
    const response = await httpClient.patch(
      `/mailboxes/${encodeURIComponent(emailAddress)}`,
      updates
    );
    return response.data;
  }

  // --- GTM Pipeline (SKY-389) ---

  async createProject(body: CreateProjectRequest): Promise<GTMProject> {
    const response = await httpClient.post('/pipeline/projects', body);
    return response.data;
  }

  async getPipelineBoard(): Promise<PipelineBoard> {
    const response = await httpClient.get('/pipeline/board');
    return response.data;
  }

  async getPipelineProject(id: string): Promise<GTMProject> {
    const response = await httpClient.get(`/pipeline/projects/${id}`);
    return response.data;
  }

  async getProjectRuns(id: string, params?: { limit?: number }): Promise<ProjectRunsResponse> {
    const response = await httpClient.get(`/pipeline/projects/${id}/runs`, { params });
    return response.data;
  }

  async getProjectBrief(id: string): Promise<CampaignBrief> {
    const response = await httpClient.get(`/pipeline/projects/${id}/brief`);
    return response.data;
  }

  async getProjectTransitions(
    id: string,
    params?: { limit?: number; before?: string }
  ): Promise<GTMProjectTransition[]> {
    const response = await httpClient.get(`/pipeline/projects/${id}/transitions`, { params });
    return response.data;
  }

  async transitionProject(
    id: string,
    toStage: PipelineStage,
    reason?: string
  ): Promise<GTMProject> {
    const response = await httpClient.post(`/pipeline/projects/${id}/transition`, {
      to_stage: toStage,
      reason,
    });
    return response.data;
  }

  async setPipelineNeedsHuman(
    id: string,
    needsHuman: boolean,
    reason?: string
  ): Promise<GTMProject> {
    const response = await httpClient.patch(`/pipeline/projects/${id}`, {
      needs_human: needsHuman,
      reason,
    });
    return response.data;
  }

  /** Point the project at one catalog conference; null clears the link. */
  async setPipelineEvent(id: string, eventId: string | null): Promise<GTMProject> {
    const response = await httpClient.patch(`/pipeline/projects/${id}`, { event_id: eventId });
    return response.data;
  }

  /** Store the engagement's ICP criteria; prefills the icp-plan run box. */
  async setPipelineIcpText(id: string, icpText: string | null): Promise<GTMProject> {
    const response = await httpClient.patch(`/pipeline/projects/${id}`, { icp_text: icpText });
    return response.data;
  }

  /** Client review state for this project's lead lists — operator mirror (SKY-540). */
  async getProjectLeadLists(id: string): Promise<ProjectLeadList[]> {
    const response = await httpClient.get(`/pipeline/projects/${id}/lead-lists`);
    return response.data;
  }

  /** Who is on one of a project's lead lists, with keep/cut per row (SKY-544). */
  async getProjectLeadListMembers(
    projectId: string,
    listId: string,
    params?: { limit?: number }
  ): Promise<LeadListMember[]> {
    const response = await httpClient.get(
      `/pipeline/projects/${projectId}/lead-lists/${listId}/members`,
      { params }
    );
    return response.data;
  }

  /** Copy versions with the client's decision + reason — operator mirror (SKY-540). */
  async getProjectCopy(id: string): Promise<ProjectCopyVariant[]> {
    const response = await httpClient.get(`/pipeline/projects/${id}/copy`);
    return response.data;
  }

  /** Skills bound to a project's current stage — the operator's run picker (SKY-552). */
  async getProjectStageSkills(id: string): Promise<StageSkillsResponse> {
    const response = await httpClient.get(`/pipeline/projects/${id}/stage-skills`);
    return response.data;
  }

  /**
   * Dispatch one skill run on the shared Claude runner (SKY-552).
   * Returns immediately with the queued run id — progress arrives via the runs panel.
   */
  async createRun(
    task: string,
    opts: { params?: Record<string, unknown>; gtmProjectId?: string } = {}
  ): Promise<{ run_id: string; status: string }> {
    const response = await httpClient.post('/runs', {
      task,
      params: opts.params,
      gtm_project_id: opts.gtmProjectId,
    });
    return response.data;
  }

  /** Answer the question a run stopped on; the follow-up run continues under the same plan. */
  async answerRun(runId: string, answer: string): Promise<{ run_id: string; status: string }> {
    const response = await httpClient.post(`/runs/${runId}/answer`, { answer });
    return response.data;
  }

  /** A fresh picture of the page a run is holding, and the address it is sitting on. */
  async getTakeoverFrame(runId: string): Promise<TakeoverFrame> {
    const response = await httpClient.post(`/runs/${runId}/takeover/frame`, undefined, {
      responseType: 'blob',
    });
    return { jpeg: response.data as Blob, url: String(response.headers?.['x-page-url'] ?? '') };
  }

  /** One input on the held page; `seq` within `stream` makes a resend apply only once. */
  async sendTakeoverInput(
    runId: string,
    event: TakeoverInput,
    ack?: { stream: string; seq: number },
  ): Promise<{ url: string; seq?: number | null }> {
    const response = await httpClient.post(`/runs/${runId}/takeover/input`, { ...event, ...ack });
    return response.data;
  }

  /** The person finished the step by hand; the follow-up run continues from the page. */
  async finishTakeover(runId: string): Promise<{ run_id: string; status: string }> {
    const response = await httpClient.post(`/runs/${runId}/takeover/done`);
    return response.data;
  }

  async appendPipelineNote(id: string, content: string): Promise<GTMProject> {
    const response = await httpClient.post(`/pipeline/projects/${id}/notes`, { content });
    return response.data;
  }

}



export const apiClient = new ApiClient();